<template>
  <div class="browser-login-page">
    <div class="browser-login-card">
      <img
        class="browser-login-logo"
        src="@/assets/image/login/logoBlue.png"
        alt="Graphchain"
      />
      <h1 class="browser-login-title">
        {{ status === "error" ? "登录失败" : "浏览器登录中" }}
      </h1>
      <p v-if="status !== 'error'" class="browser-login-desc">
        正在跳转到图链，请稍候..
      </p>
      <p v-if="status === 'error' && errorMessage" class="browser-login-error">
        {{ errorMessage }}
      </p>
      <div v-if="status === 'loading'" class="browser-login-loading">
        <span class="spinner"></span>
      </div>
      <el-button
        v-if="status === 'error'"
        type="primary"
        class="browser-login-btn"
        @click="goToNormalLogin"
      >
        返回普通登录页
      </el-button>
    </div>
  </div>
</template>

<script>
import { browserLogin } from "@/aips/login";
import {
  consumeRedirectUrl,
  restoreAuthDataFromRouteQuery,
  setNosugarSessionFlag,
  storeAuthData,
} from "@/utils/storageHelper";
import {
  getNosugarAccessToken,
  decodeNosugarPath,
  isNosugarBrowser,
  NOSUGAR_CONFIG,
} from "@/utils/nosugar";

const TAG = "[无糖Web登录]";

export default {
  name: "BrowserLoginPage",
  data() {
    return {
      status: "loading",
      errorMessage: "",
    };
  },
  async created() {
    await this.waitForSiteConfig();
    await this.handleBrowserLogin();
  },
  methods: {
    getTokenFromQuery() {
      return (this.$route.query.token || "").trim();
    },
    getPathFromQuery() {
      const pathParam = (this.$route.query.path || "").trim();
      const decodedPath = decodeNosugarPath(pathParam);
      return decodedPath || "";
    },
    buildRouteFromPath(pathValue) {
      if (!pathValue || typeof pathValue !== "string") {
        return null;
      }

      const normalizedPath = pathValue.startsWith("/")
        ? pathValue
        : `/${pathValue}`;
      const [path, queryString = ""] = normalizedPath.split("?");
      const route = { path };

      if (queryString) {
        const params = new URLSearchParams(queryString);
        const query = {};
        params.forEach((value, key) => {
          query[key] = value;
        });
        route.query = query;
      }

      return route;
    },
    cacheRouteFromPath() {
      const route = this.buildRouteFromPath(this.getPathFromQuery());
      if (!route) {
        return null;
      }

      localStorage.setItem("redirectUrl", JSON.stringify(route));
      return route;
    },
    setError(message) {
      this.status = "error";
      this.errorMessage = message || "登录失败，请重试";
      console.warn(TAG, message || "登录失败");
    },
    goToNormalLogin() {
      this.$router.replace("/login");
    },
    redirectAfterLogin() {
      this.cacheRouteFromPath();
      const redirectUrl = consumeRedirectUrl();
      if (redirectUrl) {
        this.$router.replace(redirectUrl);
        return;
      }

      this.$router.replace("/home");
    },
    async handleBrowserLogin() {
      let token = this.getTokenFromQuery();

      console.log(TAG, "开始处理浏览器专用登录页");
      console.log(TAG, "当前 URL:", window.location.href);
      console.log(TAG, "URL 参数 token:", !!token);

      // 环境检测日志
      const isWindows = /Win/.test(navigator.platform);
      console.log(TAG, "========== 环境检测 ==========");
      console.log(TAG, "Platform:", navigator.platform);
      console.log(TAG, "UserAgent:", navigator.userAgent);
      console.log(TAG, "是否 Windows:", isWindows);
      console.log(TAG, "window.nosugar 存在:", typeof window.nosugar !== 'undefined');
      console.log(TAG, "nosugar 对象 keys:", window.nosugar ? Object.keys(window.nosugar) : []);
      console.log(TAG, "检测是否无糖浏览器:", isNosugarBrowser());
      console.log(TAG, "==============================");

      if (!token) {
        restoreAuthDataFromRouteQuery(this.$route);
      }

      const hasExistingToken = !!window.localStorage.getItem("t_k");
      if (hasExistingToken) {
        console.log(TAG, "已有图链登录态，直接跳转目标页");
        setNosugarSessionFlag(true);
        this.redirectAfterLogin();
        return;
      }

      if (!token && isNosugarBrowser()) {
        try {
          console.log(TAG, "URL 未携带 token，尝试从无糖浏览器重新获取");
          token = await getNosugarAccessToken(NOSUGAR_CONFIG.APP_ID);
          console.log(TAG, "从无糖浏览器获取到 token:", !!token);
        } catch (e) {
          console.warn(TAG, "从无糖浏览器获取 token 失败:", e);
          if (isWindows) {
            this.setError("Windows 平台获取登录凭证失败，请确保无糖浏览器版本正确");
            return;
          }
        }
      }

      if (!token) {
        console.warn(TAG, "URL 中缺少 token");
        if (isWindows) {
          console.warn(TAG, "[Windows] 可能需要检查中转页是否正确传递了 token 参数");
        }
        this.setError("缺少登录凭证，请重新打开");
        return;
      }

      console.log(TAG, "准备调用后端换票接口");
      console.log(TAG, "token 前 10 位:", token.substring(0, 10) + "...");

      try {
        console.log(TAG, "调用 /v2/user/browser/login 换票");
        const response = await browserLogin({
          access_token: token,
        });

        console.log(TAG, "后端响应:", response);

        if (!response || response.code !== 200 || !response.data) {
          const backendMessage = response && (response.msg || response.message);
          const message = backendMessage
            ? `${backendMessage}，请处理后继续使用`
            : "登录失败，请处理后继续使用";
          console.warn(TAG, "浏览器登录失败:", response);
          if (isWindows) {
            console.warn(TAG, "[Windows] 后端换票失败，响应码:", response?.code);
          }
          this.setError(message);
          return;
        }

        console.log(TAG, "后端换票成功，开始存储认证数据");
        console.log(TAG, "response.data 结构:", Object.keys(response.data));

        const storeResult = storeAuthData(response.data);
        console.log(TAG, "storeAuthData 返回:", storeResult);

        if (!storeResult) {
          console.warn(TAG, "登录返回结构无法存储:", response.data);
          if (isWindows) {
            console.warn(TAG, "[Windows] 数据存储失败，请检查 localStorage 是否可用");
            console.warn(TAG, "[Windows] localStorage 可用性测试:");
            try {
              localStorage.setItem("test_key", "test_value");
              const testValue = localStorage.getItem("test_key");
              console.log(TAG, "[Windows] localStorage 读写测试:", testValue === "test_value" ? "通过" : "失败");
              localStorage.removeItem("test_key");
            } catch (storageError) {
              console.error(TAG, "[Windows] localStorage 不可用:", storageError);
            }
          }
          this.setError("登录返回结构无法存储");
          return;
        }

        console.log(TAG, "认证数据存储成功，验证 t_k:");
        const storedToken = localStorage.getItem("t_k");
        console.log(TAG, "localStorage.t_k 前 10 位:", storedToken ? storedToken.substring(0, 10) + "..." : "null");

        setNosugarSessionFlag(true);
        console.log(TAG, "浏览器登录成功，跳转系统");
        if (isWindows) {
          console.log(TAG, "[Windows] 登录流程完成，准备跳转");
        }
        this.redirectAfterLogin();
      } catch (e) {
        console.error(TAG, "浏览器登录异常:", e);
        console.error(TAG, "异常堆栈:", e.stack);
        if (isWindows) {
          console.error(TAG, "[Windows] 登录过程发生异常");
        }
        const errorMsg = e && e.message ? e.message : "登录失败";
        this.setError(isWindows ? `${errorMsg}（Windows 平台）` : errorMsg);
      }
    },
  },
};
</script>

<style lang="less" scoped>
// -------- Tokens (aligned with main login) --------
@primary: #3b82f6;
@glass-bg: rgba(37, 39, 64, 0.85);
@glass-border: rgba(215, 225, 255, 0.09);

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// -------- Page --------
.browser-login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #050505;
  color: #fff;
  font-family: "General Sans", "PingFang SC", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  position: relative;

  // Subtle dot grid — echoes the main login's dot-bg
  &::before {
    content: "";
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image: radial-gradient(circle, #a2b1ff 1px, transparent 1px);
    background-size: 24px 24px;
    opacity: 0.08;
  }
}

// -------- Card --------
.browser-login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: 40px 32px 36px;
  border: 1px solid @glass-border;
  border-radius: 20px;
  background: @glass-bg;
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  text-align: center;

  // Inner gradient overlay (same spirit as card-inner-grad)
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 20px;
    pointer-events: none;
    background: linear-gradient(to bottom, rgba(59, 130, 246, 0.1), transparent);
  }

  // All direct children sit above the gradient overlay
  > * {
    position: relative;
    z-index: 2;
  }
}

// -------- Logo --------
.browser-login-logo {
  display: block;
  width: 240px;
  max-width: 100%;
  margin: 0 auto 28px;
}

// -------- Title --------
.browser-login-title {
  margin: 0 0 10px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.3;
}

// -------- Description --------
.browser-login-desc {
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.05em;
}

// -------- Error message --------
.browser-login-error {
  margin: 12px 0 0;
  font-size: 14px;
  color: #ef4444;
  letter-spacing: 0.03em;
  line-height: 1.5;
}

// -------- Loading spinner --------
.browser-login-loading {
  margin-top: 28px;
}

.spinner {
  display: inline-block;
  width: 36px;
  height: 36px;
  border: 2.5px solid rgba(255, 255, 255, 0.12);
  border-top-color: @primary;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

// -------- Button (matched to submit-btn) --------
.browser-login-btn {
  margin-top: 28px;
  min-width: 200px;
  height: 50px;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.05em;
  font-family: inherit;

  // Override Element UI defaults → gradient CTA
  && {
    background: linear-gradient(to right, #6366f1, #2563eb);
    border: none;
    border-radius: 12px;
    color: #fff;
  }

  &:hover {
    background: linear-gradient(to right, #4f46e5, #1d4ed8) !important;
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.98);
  }
}
</style>
